import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Search, Save, Loader, ExternalLink, Globe, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * SEOSettingsEditor — admin paste-in for the SEO foundation. All values
 * are stored in `business_info` (the same key/value table that already
 * holds the business name, address, etc.) so there's no new schema.
 *
 * Fields:
 *   - seo_site_title             : sitewide HTML title fallback
 *   - seo_default_description    : meta description fallback (≤160 chars)
 *   - seo_default_og_image       : default Open Graph image URL
 *   - seo_business_type          : Schema.org LocalBusiness subtype (drives
 *                                  the JSON-LD @type on the home page)
 *   - seo_canonical_base_url     : optional override of canonical origin
 *   - seo_indexing_enabled       : 'true' | 'false' — when 'false' robots.txt
 *                                  emits Disallow:/ (use for staging)
 *
 * Helper actions:
 *   - "View sitemap" / "View robots" links open the live generated docs
 *     in a new tab so the admin can verify the output.
 */

// Curated subset of Schema.org LocalBusiness subtypes that cover the
// majority of MorphBook-relevant use cases. Plain "LocalBusiness" is
// kept as the safe default. Full list: https://schema.org/LocalBusiness
const BUSINESS_TYPES = [
  { value: 'LocalBusiness', label: 'Local Business (generic)' },
  { value: 'AnimalShelter', label: 'Animal Shelter' },
  { value: 'PetStore', label: 'Pet Store / Grooming' },
  { value: 'BeautySalon', label: 'Beauty Salon' },
  { value: 'HairSalon', label: 'Hair Salon' },
  { value: 'DaySpa', label: 'Day Spa / Wellness' },
  { value: 'HealthAndBeautyBusiness', label: 'Health & Beauty Business' },
  { value: 'MedicalBusiness', label: 'Medical Business' },
  { value: 'Dentist', label: 'Dentist' },
  { value: 'VeterinaryCare', label: 'Veterinary Care' },
  { value: 'ProfessionalService', label: 'Professional Service' },
  { value: 'PhotoStudio', label: 'Photo Studio' },
  { value: 'ChildCare', label: 'Child Care' },
  { value: 'ExerciseGym', label: 'Gym / Fitness' },
  { value: 'AutomotiveBusiness', label: 'Automotive Business' },
];

const FIELDS = [
  {
    key: 'seo_site_title',
    label: 'Site title',
    placeholder: 'Acme Grooming — Toronto',
    helpText: 'Shown in browser tabs, search results, and social shares. Aim for 50–60 characters; include your service + location for local SEO.',
    type: 'text',
  },
  {
    key: 'seo_default_description',
    label: 'Default meta description',
    placeholder: 'Professional dog grooming in downtown Toronto. Book online in seconds.',
    helpText: 'The grey snippet under the page title in Google. 150–160 characters works best. Pages can override this individually.',
    type: 'textarea',
    maxLength: 200,
  },
  {
    key: 'seo_default_og_image',
    label: 'Default Open Graph image URL',
    placeholder: 'https://example.com/og-image.jpg',
    helpText: 'The image shown when your link is shared on Facebook / X / LinkedIn / iMessage. Recommended size: 1200×630 px. Falls back to your business logo if blank.',
    type: 'text',
  },
  {
    key: 'seo_canonical_base_url',
    label: 'Canonical site URL (optional)',
    placeholder: 'https://acmegrooming.com',
    helpText: 'Leave blank in most cases — the system auto-detects this from the request. Override only if your public domain differs from where this admin panel is hosted.',
    type: 'text',
  },
];

const SEOSettingsEditor = () => {
  const [values, setValues] = useState({
    seo_site_title: '',
    seo_default_description: '',
    seo_default_og_image: '',
    seo_business_type: 'LocalBusiness',
    seo_canonical_base_url: '',
    seo_indexing_enabled: 'true',
  });
  const [original, setOriginal] = useState(values);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/seo/settings`)
      .then((res) => {
        const next = {
          seo_site_title: res.data?.seo_site_title || '',
          seo_default_description: res.data?.seo_default_description || '',
          seo_default_og_image: res.data?.seo_default_og_image || '',
          seo_business_type: res.data?.seo_business_type || 'LocalBusiness',
          seo_canonical_base_url: res.data?.seo_canonical_base_url || '',
          seo_indexing_enabled: (res.data?.seo_indexing_enabled || 'true').toString(),
        };
        setValues(next);
        setOriginal(next);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const dirty = JSON.stringify(values) !== JSON.stringify(original);
  const indexingDisabled = values.seo_indexing_enabled === 'false';

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      // Each value goes through the existing business_info save endpoint.
      // We do them serially to keep the admin-side error handling simple.
      for (const [key, value] of Object.entries(values)) {
        await axios.post(
          `${API_URL}/business-info/${key}`,
          { value: value || '' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setOriginal(values);
      toast.success('SEO settings saved. Public pages will pick them up on their next reload.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center text-gray-500 py-6 justify-center">
          <Loader size={18} className="animate-spin mr-2" />
          Loading SEO settings...
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Search className="text-primary-600" size={24} />
        <h3 className="text-xl font-bold text-gray-900">Search Engine Optimization (SEO)</h3>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Controls how your public booking page appears in Google, Bing, and social-media link previews.
        These values feed the page <code>&lt;title&gt;</code>, meta description, Open Graph
        / Twitter Card tags, and Schema.org JSON-LD structured data injected on every public page.
      </p>

      {indexingDisabled && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900 flex items-start">
          <AlertTriangle size={16} className="mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Indexing is disabled.</strong> The generated <code>/robots.txt</code> currently emits
            <code> Disallow: /</code> for all crawlers. Use this only for staging or while you're still
            populating content. Switch to <em>Allow indexing</em> below before going live.
          </div>
        </div>
      )}

      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-semibold text-gray-700 mb-1">{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea
                value={values[f.key]}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value.slice(0, f.maxLength || 500) }))}
                placeholder={f.placeholder}
                rows={2}
                className="input w-full"
              />
            ) : (
              <input
                type="text"
                value={values[f.key]}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="input w-full"
              />
            )}
            <p className="text-xs text-gray-500 mt-1">{f.helpText}</p>
            {f.key === 'seo_default_description' && (
              <p className="text-xs text-gray-400 mt-0.5">
                {values.seo_default_description.length} characters
                {values.seo_default_description.length > 160 && ' — Google may truncate over 160'}
              </p>
            )}
          </div>
        ))}

        {/* Business type selector */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Business type (Schema.org LocalBusiness subtype)
          </label>
          <select
            value={values.seo_business_type}
            onChange={(e) => setValues((v) => ({ ...v, seo_business_type: e.target.value }))}
            className="input w-full"
          >
            {BUSINESS_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Drives the structured-data type Google uses when categorizing your page. Pick the closest
            match to what you actually do — better targeting often improves local-search rankings.{' '}
            <a
              href="https://schema.org/LocalBusiness"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 inline-flex items-center"
            >
              Full list <ExternalLink size={11} className="ml-0.5" />
            </a>
          </p>
        </div>

        {/* Indexing toggle */}
        <div className="border-t pt-4 mt-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Search engine indexing</label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                value="true"
                checked={values.seo_indexing_enabled !== 'false'}
                onChange={() => setValues((v) => ({ ...v, seo_indexing_enabled: 'true' }))}
                className="mr-2"
              />
              <span className="text-sm">Allow indexing (recommended for production)</span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                value="false"
                checked={values.seo_indexing_enabled === 'false'}
                onChange={() => setValues((v) => ({ ...v, seo_indexing_enabled: 'false' }))}
                className="mr-2"
              />
              <span className="text-sm">Disallow (staging / private)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Quick view links */}
      <div className="mt-6 flex items-center flex-wrap gap-2">
        <a
          href="/sitemap.xml"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
        >
          <Globe size={14} className="mr-1" /> View live sitemap.xml
        </a>
        <a
          href="/robots.txt"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
        >
          <Globe size={14} className="mr-1" /> View live robots.txt
        </a>
      </div>

      <div className="mt-6 flex items-center justify-between border-t pt-4">
        <p className="text-xs text-gray-500 max-w-md">
          Changes apply immediately to <code>/sitemap.xml</code>, <code>/robots.txt</code>, and meta tags.
          Search engines re-crawl on their own schedule (typically days to weeks).
        </p>
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className={`inline-flex items-center px-4 py-2 text-sm rounded ${
            dirty
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? <Loader size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
          {saving ? 'Saving...' : dirty ? 'Save SEO settings' : 'Saved'}
        </button>
      </div>
    </div>
  );
};

export default SEOSettingsEditor;
